import React, { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { DevicePhoneMobileIcon, PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import { inviteContactsViaSms } from '../../utils/contacts';
import { referralUrlForLink } from '../../utils/referralLinks';
import en from '../../i18n/en';
import es from '../../i18n/es';

const CONTACT_AVATARS = [
  { initials: 'AM', className: 'from-violet-500 to-purple-600' },
  { initials: 'JR', className: 'from-emerald-500 to-teal-600' },
  { initials: 'LS', className: 'from-rose-500 to-pink-600' },
];

const MESSAGE_LOCALES = [
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
];

const springSnappy = { type: 'spring', stiffness: 380, damping: 28 };

function buildShareMessage(locale, url, formatMessage) {
  const template =
    locale === 'es' ? es.enrola.defaultShareClients : en.enrola.defaultShareClients;
  return formatMessage(template, { url });
}

function MessageLanguageSwitch({ value, onChange, label, reduceMotion, compact = false }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-secondary-500 ">
        {label}
      </span>
      <div
        role="group"
        aria-label={label}
        className="relative flex rounded-lg bg-sky-100/80 p-0.5 "
      >
        {MESSAGE_LOCALES.map(({ code, label: localeLabel }) => {
          const active = value === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => onChange(code)}
              aria-pressed={active}
              className={`relative z-10 min-w-[2rem] rounded-md px-1.5 py-0.5 text-[10px] font-bold transition-colors ${
                compact ? 'min-w-[1.85rem]' : 'min-w-[2.25rem] py-1'
              } ${
                active
                  ? 'text-sky-900 '
                  : 'text-secondary-500 hover:text-secondary-700  '
              }`}
            >
              {active && !reduceMotion ? (
                <motion.span
                  layoutId={compact ? 'invite-message-lang-pill-compact' : 'invite-message-lang-pill'}
                  className="absolute inset-0 rounded-md bg-white shadow-sm "
                  transition={springSnappy}
                />
              ) : active ? (
                <span className="absolute inset-0 rounded-md bg-white shadow-sm " />
              ) : null}
              <span className="relative">{localeLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InviteContactsMiniCard({ link, className = '', compact = false }) {
  const { t, formatMessage, language } = useLanguage();
  const ic = t.enrola.inviteContacts;
  const mc = ic.miniCard;
  const reduceMotion = useReducedMotion();
  const [loading, setLoading] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [messageLang, setMessageLang] = useState(() => (language === 'es' ? 'es' : 'en'));

  const hasLink = Boolean(link?.link_code || link?.code);
  const fullUrl = hasLink ? referralUrlForLink(link) : '';
  const shareMessage = useMemo(
    () => (fullUrl ? buildShareMessage(messageLang, fullUrl, formatMessage) : ''),
    [messageLang, fullUrl, formatMessage]
  );

  if (!hasLink) {
    return null;
  }

  const handleInvite = async () => {
    if (loading) return;

    setLoading(true);
    try {
      const result = await inviteContactsViaSms(shareMessage);

      if (result.method === 'contact-picker-sms') {
        toast.success(formatMessage(ic.openingSms, { count: result.count }));
      } else if (result.method === 'sms-compose') {
        toast.success(ic.openingMessages);
      }
    } catch (error) {
      if (error?.message === 'CANCELLED') {
        return;
      }

      if (error?.message === 'DESKTOP_UNSUPPORTED') {
        try {
          await copyTextToClipboard(shareMessage);
          toast.success(ic.desktopCopied);
        } catch {
          toast.error(ic.failed);
        }
        return;
      }

      toast.error(ic.failed);
    } finally {
      setLoading(false);
      setPressed(false);
    }
  };

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...springSnappy, delay: 0 }}
        className={`relative flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-sky-200/70 bg-gradient-to-br from-sky-50 via-white to-blue-50/80 p-3.5 shadow-soft     ${className}`}
      >
        <div className="relative flex shrink-0 items-start gap-2.5">
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/30">
            <DevicePhoneMobileIcon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600 ">
              {mc.eyebrow}
            </p>
            <h4 className="text-xs font-bold leading-snug text-secondary-900 ">
              {mc.title}
            </h4>
            <p className="mt-0.5 line-clamp-1 text-[10px] leading-snug text-secondary-600 ">
              {mc.body}
            </p>
          </div>
        </div>

        <div className="mt-2.5 flex shrink-0 items-center gap-2">
          <div className="flex items-center">
            {CONTACT_AVATARS.map((avatar, index) => (
              <div
                key={avatar.initials}
                className={`flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br text-[9px] font-bold text-white ring-2 ring-white  ${avatar.className} ${
                  index > 0 ? '-ml-2' : ''
                }`}
              >
                {avatar.initials}
              </div>
            ))}
            <div className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-[9px] font-bold text-sky-700 ring-2 ring-white   ">
              +∞
            </div>
          </div>
          <p className="min-w-0 flex-1 truncate text-[10px] font-medium text-secondary-500 ">
            {mc.contactsHint}
          </p>
        </div>

        <div className="mt-2.5 flex min-h-0 flex-1 flex-col gap-2 overflow-hidden rounded-lg border border-sky-100/80 bg-white/60 p-2.5  ">
          <MessageLanguageSwitch
            value={messageLang}
            onChange={setMessageLang}
            label={mc.messageLanguage}
            reduceMotion={reduceMotion}
            compact
          />
          <motion.p
            key={messageLang}
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="line-clamp-3 min-h-0 flex-1 overflow-hidden text-[10px] leading-snug text-secondary-700 "
          >
            {shareMessage}
          </motion.p>
        </div>

        <motion.button
          type="button"
          onClick={handleInvite}
          disabled={loading}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          onMouseLeave={() => setPressed(false)}
          whileHover={reduceMotion ? undefined : { scale: 1.02 }}
          whileTap={reduceMotion ? undefined : { scale: 0.98 }}
          transition={springSnappy}
          className="relative mt-2.5 flex w-full shrink-0 items-center justify-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 px-3 py-2.5 text-xs font-semibold text-white shadow-md shadow-sky-500/25 disabled:cursor-wait disabled:opacity-80"
        >
          {loading ? (
            <>
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
                className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white"
              />
              {ic.loading}
            </>
          ) : (
            <>
              <PaperAirplaneIcon
                className={`h-3.5 w-3.5 shrink-0 transition-transform ${pressed ? 'translate-x-0.5 -translate-y-0.5' : ''}`}
              />
              <span className="truncate">{ic.label}</span>
              <SparklesIcon className="h-3.5 w-3.5 shrink-0 opacity-80" />
            </>
          )}
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springSnappy, delay: 0 }}
      className={`relative flex flex-col overflow-hidden rounded-2xl border border-sky-200/70 bg-gradient-to-br from-sky-50 via-white to-blue-50/80 p-4 shadow-soft     ${className}`}
    >
      {!reduceMotion && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-sky-400/20 blur-2xl "
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.65, 0.4] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      <div className="relative flex items-start gap-3">
        <div className="relative shrink-0">
          {!reduceMotion && (
            <motion.span
              aria-hidden
              className="absolute inset-0 rounded-xl bg-sky-400/30"
              animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0, 0.55] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}
          <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-md shadow-sky-500/30">
            <DevicePhoneMobileIcon className="h-6 w-6" />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wider text-sky-600 ">
            {mc.eyebrow}
          </p>
          <h4 className="mt-0.5 text-sm font-bold leading-snug text-secondary-900 ">
            {mc.title}
          </h4>
          <p className="mt-1 text-xs leading-relaxed text-secondary-600 ">
            {mc.body}
          </p>
        </div>
      </div>

      <div className="relative mt-4 flex flex-col gap-2 rounded-xl border border-sky-100/80 bg-white/70 px-3 py-2.5   sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center">
          {CONTACT_AVATARS.map((avatar, index) => (
            <motion.div
              key={avatar.initials}
              initial={reduceMotion ? false : { opacity: 0, scale: 0.6, x: -8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ ...springSnappy, delay: 0.12 + index * 0.06 }}
              className={`flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br text-[10px] font-bold text-white ring-2 ring-white  ${avatar.className} ${
                index > 0 ? '-ml-2.5' : ''
              }`}
            >
              {avatar.initials}
            </motion.div>
          ))}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...springSnappy, delay: 0.3 }}
            className="-ml-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-[10px] font-bold text-sky-700 ring-2 ring-white   "
          >
            +∞
          </motion.div>
        </div>

        <p className="text-[10px] font-medium leading-snug text-secondary-500  sm:max-w-[9rem] sm:text-right">
          {mc.contactsHint}
        </p>
      </div>

      <div className="mt-3 space-y-2 rounded-xl border border-sky-100/80 bg-white/60 p-2.5  ">
        <MessageLanguageSwitch
          value={messageLang}
          onChange={setMessageLang}
          label={mc.messageLanguage}
          reduceMotion={reduceMotion}
        />
        <motion.p
          key={messageLang}
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          className="line-clamp-3 text-[11px] leading-relaxed text-secondary-700 "
        >
          {shareMessage}
        </motion.p>
      </div>

      <div className="pt-3">
        <motion.button
          type="button"
          onClick={handleInvite}
          disabled={loading}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          onMouseLeave={() => setPressed(false)}
          whileHover={reduceMotion ? undefined : { scale: 1.02 }}
          whileTap={reduceMotion ? undefined : { scale: 0.98 }}
          transition={springSnappy}
          className="relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-500/25 disabled:cursor-wait disabled:opacity-80"
        >
          {!reduceMotion && !loading && (
            <motion.span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent"
              animate={{ x: ['-120%', '120%'] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.8 }}
            />
          )}
          {loading ? (
            <>
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
                className="inline-block h-4 w-4 rounded-full border-2 border-white/30 border-t-white"
              />
              {ic.loading}
            </>
          ) : (
            <>
              <PaperAirplaneIcon
                className={`h-4 w-4 shrink-0 transition-transform ${pressed ? 'translate-x-0.5 -translate-y-0.5' : ''}`}
              />
              {ic.label}
              <SparklesIcon className="h-4 w-4 shrink-0 opacity-80" />
            </>
          )}
        </motion.button>

        <p className="mt-2 text-center text-[10px] text-secondary-400 ">{mc.footerHint}</p>
      </div>
    </motion.div>
  );
}

export default InviteContactsMiniCard;
